import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";
import { formatShortWeekdayDate, formatTime } from "@shared";

import { AppointmentExchangeOfferResponse, AppointmentExchangeOfferStatus } from "@/api";
import { AppointmentPurposeChip } from "@/common/components/appointments/shared/components/appointment-purpose-chip/AppointmentPurposeChip";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { formatCounselorLabel } from "@/common/utils/formatCounselorLabel";
import { useTranslation } from "@/translations";

import { acceptedOfferCardSx, offerCardSx } from "../ExchangeOffersDialog.styles";
import { counselorInitials } from "../ExchangeOffersDialog.utils";
import { ExchangeOfferStatusChip } from "./ExchangeOfferStatusChip";

interface Props {
  offer: AppointmentExchangeOfferResponse;
  isBusy?: boolean;
  /** Present on open offers only; decided offers show their status chip instead of the buttons. */
  onAccept?: (offerId: string) => void;
  onReject?: (offerId: string) => void;
}

/**
 * One counter-offer: who offers which Termin, their note, and either the two decisions or the outcome.
 * A pending offer whose Termin was deleted meanwhile can only be rejected.
 */
export function ExchangeOfferCard({ offer, isBusy = false, onAccept, onReject }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const labels = useAppointmentLabels();

  const locale = locales[language];
  const counselor = {
    userCode: offer.offeredByCounselorUserCode,
    firstName: offer.offeredByCounselorFirstName,
    lastName: offer.offeredByCounselorLastName,
  };
  const counselorLabel = formatCounselorLabel(counselor);
  const isPending = offer.status === AppointmentExchangeOfferStatus.Pending;
  const isAccepted = offer.status === AppointmentExchangeOfferStatus.Accepted;
  const appointment = offer.offeredAppointment;
  const location = appointment?.location ?? null;

  return (
    <Box display="flex" alignItems="center" gap={2} sx={isAccepted ? acceptedOfferCardSx : offerCardSx}>
      <Avatar alt="" aria-hidden sx={{ bgcolor: isPending ? "primary.main" : "action.disabled" }}>
        {counselorInitials(counselor)}
      </Avatar>

      <Stack spacing={0.5} flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={700}>
          {counselorLabel}
        </Typography>
        {appointment ? (
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="body2" fontWeight={700}>
              {formatShortWeekdayDate(appointment.startTimeUtc, locale)}
            </Typography>
            <Typography variant="body2">
              {appointment.isAllDay
                ? t("exchange.allDay")
                : `${formatTime(appointment.startTimeUtc, locale)} ${t("common.clock")}`}
            </Typography>
            <AppointmentPurposeChip label={labels.purposeLabelOf(appointment.purposeKey)} />
            {location && <Typography variant="body2">{location}</Typography>}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("exchange.offersDialog.appointmentMissing")}
          </Typography>
        )}
        {offer.note && (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t("exchange.offersDialog.note", { note: offer.note })}
          </Typography>
        )}
      </Stack>

      {isPending ? (
        <Stack direction="row" spacing={1} flexShrink={0}>
          {appointment && (
            <Button
              variant="contained"
              startIcon={<SwapHorizRoundedIcon />}
              disabled={isBusy}
              aria-label={t("exchange.offersDialog.acceptOfferAriaLabel", { counselor: counselorLabel })}
              onClick={() => onAccept?.(offer.id)}
            >
              {t("exchange.offersDialog.acceptOffer")}
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseRoundedIcon />}
            disabled={isBusy}
            aria-label={t("exchange.offersDialog.rejectOfferAriaLabel", { counselor: counselorLabel })}
            onClick={() => onReject?.(offer.id)}
          >
            {t("exchange.offersDialog.rejectOffer")}
          </Button>
        </Stack>
      ) : (
        <ExchangeOfferStatusChip status={offer.status} />
      )}
    </Box>
  );
}
